/**
 * ============================================================================
 * cro-cart.js — Tarooet cart drawer (desktop side panel + mobile bottom sheet)
 * ============================================================================
 *
 * Self-contained. No @theme/* imports, no Section Rendering API. This is now
 * the theme's only cart UI — the native Horizon cart drawer (theme-drawer
 * #cart-drawer) has been removed entirely; its markup no longer renders.
 * One source of truth: the `cart` object from /cart.js. The drawer is
 * re-rendered from it wholesale — never patched.
 *
 * Active at every width via MOBILE_ENABLED (see active() below). Geometry
 * differs per breakpoint (side panel >=990px, bottom sheet <990px, see
 * snippets/cro-cart-drawer.liquid) but the JS state machine is shared.
 *
 * ---------------------------------------------------------------------------
 * killNativeDrawer() / watchForNativeDrawer() — why they're still here
 * ---------------------------------------------------------------------------
 * The native drawer's files are gone, so these are now defensive no-ops:
 * cheap insurance against a rogue app re-injecting a `#cart-drawer` element
 * (e.g. via Shopify.actions.openCart from an app that hasn't been updated).
 * ============================================================================
 */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------- */
  /* Config                                                                  */
  /* ---------------------------------------------------------------------- */

  var DESKTOP_MIN = 990; // px — matches the theme's desktop breakpoint
  var CLOSE_MS = 480; // must match the CSS transition duration

  // Mobile rollout switch — flip to true once the <989px bottom-sheet CSS
  // and markup are verified. While false, active() === isDesktop() exactly,
  // so this file's behavior below 990px is untouched (native drawer stays
  // in control). Flipping it back to false is an instant, safe rollback.
  var MOBILE_ENABLED = true;

  var root = null;
  var viewPreview = null;
  var viewFull = null;
  var toast = null;

  var cart = null; // single source of truth: the /cart.js payload
  var lastAddedKey = null;
  var busy = false;
  var lastFocused = null;
  var closeTimer = null;
  var savedScroll = { html: '', body: '', pad: '' };
  var productCache = {}; // handle -> product JSON (for compare-at prices)

  var discountDraft = ''; // survives re-renders so typing is never lost
  var discountMessage = '';
  var discountOpen = false; // accordion state, also survives re-renders

  var cfg = {};

  /* ---------------------------------------------------------------------- */
  /* Small helpers                                                           */
  /* ---------------------------------------------------------------------- */

  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN;
  }

  // Whether CroCart should be driving the cart UI at the current width —
  // always true on desktop, true on mobile only once MOBILE_ENABLED is on.
  function active() {
    return MOBILE_ENABLED || isDesktop();
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Ask the Shopify CDN for a right-sized image instead of the full-res one. */
  function imgUrl(url, width) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /**
   * Formats cents using the shop's money_format string.
   * Supports every separator variant Shopify emits.
   */
  function money(cents) {
    var format = cfg.moneyFormat || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function group(value, precision, thousands, decimal) {
      if (isNaN(value) || value == null) value = 0;
      var fixed = (value / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var match = format.match(placeholder);
    var name = match ? match[1] : 'amount';
    var value;

    switch (name) {
      case 'amount_no_decimals':
        value = group(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = group(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = group(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = group(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = group(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = group(cents, 0, ' ', ',');
        break;
      default:
        value = group(cents, 2, ',', '.');
    }

    return format.replace(placeholder, value);
  }

  /* ---------------------------------------------------------------------- */
  /* Kill the native Horizon cart drawer (desktop only)                      */
  /* ---------------------------------------------------------------------- */

  function killNativeDrawer() {
    if (!active()) return;

    var drawer = document.getElementById('cart-drawer');
    if (drawer) {
      try {
        if (typeof drawer.close === 'function') drawer.close();
      } catch (e) {
        /* the element may not have upgraded yet — removal is enough */
      }
      var section = drawer.closest('#shopify-section-cart-drawer-section') || drawer;
      section.remove();
    }

    var orphan = document.getElementById('shopify-section-cart-drawer-section');
    if (orphan) orphan.remove();
  }

  function watchForNativeDrawer() {
    if (!window.MutationObserver) return;

    var observer = new MutationObserver(function () {
      if (!active()) return;
      if (document.getElementById('cart-drawer') || document.getElementById('shopify-section-cart-drawer-section')) {
        killNativeDrawer();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------------- */
  /* Icons                                                                   */
  /* ---------------------------------------------------------------------- */

  var ICON_CHECK =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" stroke-width="2.2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var ICON_CLOSE =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round"/></svg>';

  var ICON_ARROW =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var ICON_CHEVRON =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M6 9.5l6 6 6-6" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* ---------------------------------------------------------------------- */
  /* Pricing helpers                                                         */
  /* ---------------------------------------------------------------------- */

  /**
   * /cart.js does not expose compare_at_price, so we pull it from
   * /products/{handle}.js and cache per session. Deduplicated by handle and
   * browser-cached, so a repeat render costs nothing.
   */
  function fetchProduct(handle) {
    if (Object.prototype.hasOwnProperty.call(productCache, handle)) {
      return Promise.resolve(productCache[handle]);
    }

    return fetch(cfg.rootUrl + 'products/' + handle + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (product) {
        productCache[handle] = product;
        return product;
      });
  }

  /** Attaches `_compareAt` (per-unit cents, or null) to every line. */
  function enrich(data) {
    if (!data || !data.items || !data.items.length) return Promise.resolve(data);

    var handles = [];
    data.items.forEach(function (item) {
      if (item.handle && handles.indexOf(item.handle) === -1) handles.push(item.handle);
    });

    return Promise.all(handles.map(fetchProduct)).then(function (products) {
      var compareByVariant = {};

      products.forEach(function (product) {
        if (!product || !product.variants) return;
        product.variants.forEach(function (variant) {
          compareByVariant[variant.id] = variant.compare_at_price;
        });
      });

      data.items.forEach(function (item) {
        var compareAt = compareByVariant[item.variant_id];
        item._compareAt = compareAt && compareAt > item.final_price ? compareAt : null;
      });

      return data;
    });
  }

  function unitOriginal(item) {
    return item._compareAt && item._compareAt > item.original_price ? item._compareAt : item.original_price;
  }

  /**
   * "Total before discounts" = the highest defensible original price per line:
   * the compare-at price when there is one, otherwise the pre-discount price.
   */
  function totals() {
    var original = 0;

    cart.items.forEach(function (item) {
      original += unitOriginal(item) * item.quantity;
    });

    return {
      original: original,
      savings: Math.max(0, original - cart.total_price),
    };
  }

  /** Crossed-out line total, when the line is discounted. */
  function lineCompareHtml(item) {
    var lineOriginal = unitOriginal(item) * item.quantity;
    if (lineOriginal <= item.final_line_price) return '';
    return '<s class="crocart-line__compare">' + money(lineOriginal) + '</s>';
  }

  /* ---------------------------------------------------------------------- */
  /* Discount codes                                                          */
  /* ---------------------------------------------------------------------- */

  /** Titles of any discount codes currently applied to the cart. */
  function appliedCodes() {
    var applications = (cart && (cart.discount_applications || cart.cart_level_discount_applications)) || [];
    return applications
      .filter(function (application) {
        return !application.type || application.type === 'discount_code';
      })
      .map(function (application) {
        return application.title;
      });
  }

  /**
   * The Ajax Cart API has no endpoint for discount codes. The supported route
   * is GET /discount/{code}, which sets the discount cookie server-side and
   * then redirects. Pointing that redirect at /cart.js means the single
   * request both applies the code and returns the recalculated cart.
   */
  function applyDiscount(code) {
    code = String(code || '').trim();
    if (!code || busy) return;

    busy = true;
    root.setAttribute('data-busy', '');
    discountMessage = '';

    var target = cfg.rootUrl + 'cart.js';
    var url = cfg.rootUrl + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(target);

    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        return response.json();
      })
      .then(enrich)
      .then(function (data) {
        cart = data;
        updateBubble(cart.item_count);

        var matched = appliedCodes().some(function (title) {
          return String(title).toUpperCase() === code.toUpperCase();
        });

        if (matched) {
          // Success: clear the field and fold the accordion away — the green
          // pill above the toggle is the confirmation from here on.
          discountDraft = '';
          discountMessage = '';
          discountOpen = false;
        } else {
          discountMessage = cfg.discountErrorLabel;
          discountOpen = true; // keep it open so the reason stays visible
        }

        renderAll();
      })
      .catch(function () {
        discountMessage = cfg.discountErrorLabel;
        discountOpen = true;
        return refresh();
      })
      .finally(function () {
        busy = false;
        root.removeAttribute('data-busy');
      });
  }

  function discountHtml() {
    var codes = appliedCodes();

    var pills = codes
      .map(function (title) {
        return '<span class="crocart-disc__pill">' + ICON_CHECK + esc(title) + '</span>';
      })
      .join('');

    var message = discountMessage
      ? '<p class="crocart-disc__msg" role="status">' + esc(discountMessage) + '</p>'
      : '';

    // Applied codes sit OUTSIDE the collapsible so an active discount is
    // always visible, even when the accordion is folded shut.
    return (
      '<div class="crocart-disc"' + (discountOpen ? ' data-open' : '') + '>' +
      (pills ? '<div class="crocart-disc__pills">' + pills + '</div>' : '') +
      '<button type="button" class="crocart-disc__toggle" data-cc-disc-toggle ' +
      'aria-expanded="' + (discountOpen ? 'true' : 'false') + '" aria-controls="CroCartDiscPanel">' +
      '<span>' + esc(cfg.discountToggleLabel) + '</span>' + ICON_CHEVRON +
      '</button>' +
      '<div class="crocart-disc__panel" id="CroCartDiscPanel">' +
      '<div class="crocart-disc__panel-inner">' +
      '<div class="crocart-disc__row">' +
      '<input type="text" class="crocart-disc__input" data-cc-disc-input ' +
      'value="' + esc(discountDraft) + '" ' +
      'placeholder="' + esc(cfg.discountPlaceholder) + '" ' +
      'aria-label="' + esc(cfg.discountPlaceholder) + '" ' +
      'autocomplete="off" autocapitalize="characters" spellcheck="false">' +
      '<button type="button" class="crocart-disc__btn" data-cc-disc-apply>' + esc(cfg.applyLabel) + '</button>' +
      '</div>' +
      message +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Free shipping progress                                                  */
  /* ---------------------------------------------------------------------- */

  function shippingState() {
    if (!cfg.shippingEnabled || !cfg.shippingThreshold) return null;
    if (!cart || cart.item_count === 0) return null;

    var remaining = Math.max(0, cfg.shippingThreshold - cart.total_price);

    return {
      remaining: remaining,
      unlocked: remaining === 0,
      percent: Math.min(100, (cart.total_price / cfg.shippingThreshold) * 100),
    };
  }

  /**
   * Centred indicator block that sits directly under the header: one legible
   * line of copy above a compact pill gauge (~1/3 of the panel width). The
   * block carries the single hairline that divides it from the items list, so
   * the header itself has no border — one rule, never two stacked.
   */
  function shippingBlockHtml(state) {
    if (!state) return '';

    var messageHtml;
    if (state.unlocked) {
      messageHtml = esc(cfg.shipUnlocked);
    } else {
      var tpl = String(cfg.shipProgress || '');
      var idx = tpl.indexOf('__AMOUNT__');
      if (idx === -1) {
        messageHtml = esc(tpl);
      } else {
        messageHtml =
          esc(tpl.slice(0, idx)) +
          '<span class="crocart-ship__amount">' + esc(money(state.remaining)) + '</span>' +
          esc(tpl.slice(idx + '__AMOUNT__'.length));
      }
    }

    return (
      '<div class="crocart-ship' + (state.unlocked ? ' crocart-ship--done' : '') + '">' +
      '<p class="crocart-ship__msg" role="status">' + messageHtml + '</p>' +
      '<div class="crocart-ship__track" ' +
      'role="progressbar" aria-valuenow="' + Math.round(state.percent) + '" aria-valuemin="0" aria-valuemax="100" ' +
      'aria-label="' + esc(cfg.shipLabel) + '">' +
      '<div class="crocart-ship__fill" style="width:' + state.percent + '%"></div>' +
      '</div>' +
      '</div>'
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Totals block                                                            */
  /* ---------------------------------------------------------------------- */

  function totalsHtml() {
    var sums = totals();
    var rows = '';

    if (sums.savings > 0) {
      rows +=
        '<div class="crocart-total crocart-total--strike">' +
        '<span class="crocart-total__label">' + esc(cfg.beforeDiscountsLabel) + '</span>' +
        '<span class="crocart-total__value"><s>' + money(sums.original) + '</s></span>' +
        '</div>' +
        '<div class="crocart-total crocart-total--savings">' +
        '<span class="crocart-total__label">' + esc(cfg.savingsLabel) + '</span>' +
        '<span class="crocart-total__value">&minus;' + money(sums.savings) + '</span>' +
        '</div>';
    }

    rows +=
      '<div class="crocart-total crocart-total--grand">' +
      '<span class="crocart-total__label">' + esc(cfg.subtotalLabel) + '</span>' +
      '<span class="crocart-total__value">' + money(cart.total_price) + '</span>' +
      '</div>';

    return rows;
  }

  /**
   * Savings on just the added line — the preview card is a "this product was
   * added" confirmation, not a cart summary, so it shows per-item savings
   * rather than the cart-wide totals block. Empty when the line has no
   * discount, so we never show a "You save $0.00" line.
   */
  function itemSavingsHtml(item) {
    var lineOriginal = unitOriginal(item) * item.quantity;
    var savings = lineOriginal - item.final_line_price;
    if (savings <= 0) return '';

    return (
      '<div class="crocart-totals">' +
      '<div class="crocart-total crocart-total--savings">' +
      '<span class="crocart-total__label">' + esc(cfg.savingsLabel) + '</span>' +
      '<span class="crocart-total__value">&minus;' + money(savings) + '</span>' +
      '</div>' +
      '</div>'
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Rendering — PREVIEW state                                               */
  /* ---------------------------------------------------------------------- */

  function renderPreview() {
    if (!cart) return;

    // Resolve the added line from the live cart so it carries the enriched
    // compare-at price and the true merged quantity.
    var item = null;
    if (lastAddedKey) {
      for (var i = 0; i < cart.items.length; i++) {
        if (cart.items[i].key === lastAddedKey) {
          item = cart.items[i];
          break;
        }
      }
    }

    var ship = shippingState();
    var body = '';
    var savingsRow = '';

    if (item) {
      var variant =
        item.variant_title && item.variant_title !== 'Default Title'
          ? '<p class="crocart-line__variant">' + esc(item.variant_title) + '</p>'
          : '';

      body =
        '<div class="crocart-added">' +
        '<div class="crocart-added__media">' +
        (item.image
          ? '<img src="' + esc(imgUrl(item.image, 240)) + '" alt="' + esc(item.product_title) + '" width="96" height="96" loading="eager">'
          : '<div class="crocart-added__placeholder"></div>') +
        '</div>' +
        '<div class="crocart-added__info">' +
        '<p class="crocart-line__title crocart-line__title--clamp" title="' + esc(item.product_title) + '">' +
        esc(item.product_title) +
        '</p>' +
        variant +
        '<p class="crocart-line__meta">' + esc(cfg.qtyLabel) + ' ' + item.quantity + '</p>' +
        '</div>' +
        '<div class="crocart-added__right">' +
        '<p class="crocart-line__price">' + lineCompareHtml(item) + money(item.final_line_price) + '</p>' +
        '</div>' +
        '</div>';

      savingsRow = itemSavingsHtml(item);
    }

    viewPreview.innerHTML =
      '<header class="crocart__head' + (ship ? '' : ' crocart__head--ruled') + '">' +
      '<div class="crocart-success">' +
      '<span class="crocart-success__badge">' + ICON_CHECK + '</span>' +
      '<span class="crocart-success__text">' + esc(cfg.addedTitle) + '</span>' +
      '</div>' +
      '<button type="button" class="crocart__close" data-cc-close aria-label="' + esc(cfg.closeLabel) + '">' +
      ICON_CLOSE +
      '</button>' +
      '</header>' +
      shippingBlockHtml(ship) +
      '<div class="crocart__body">' + body + '</div>' +
      '<footer class="crocart__foot">' +
      savingsRow +
      '<button type="button" class="crocart-btn crocart-btn--primary" data-cc-expand>' +
      '<span>' + esc(cfg.viewCartLabel) + '</span>' + ICON_ARROW +
      '</button>' +
      '</footer>';
  }

  /* ---------------------------------------------------------------------- */
  /* Rendering — FULL state                                                  */
  /* ---------------------------------------------------------------------- */

  function renderFull() {
    if (!cart) return;

    var count = cart.item_count;
    var ship = shippingState();

    var head =
      '<header class="crocart__head crocart__head--full' + (ship ? '' : ' crocart__head--ruled') + '">' +
      '<h2 class="crocart__heading">' + esc(cfg.cartTitle) +
      '<span class="crocart__count">' + count + '</span></h2>' +
      '<button type="button" class="crocart__close" data-cc-close aria-label="' + esc(cfg.closeLabel) + '">' +
      ICON_CLOSE +
      '</button>' +
      '</header>' +
      shippingBlockHtml(ship);

    if (!count) {
      viewFull.innerHTML =
        head +
        '<div class="crocart__body crocart__body--empty">' +
        '<p class="crocart-empty__title">' + esc(cfg.emptyTitle) + '</p>' +
        '<button type="button" class="crocart-btn crocart-btn--primary" data-cc-close>' +
        esc(cfg.continueLabel) +
        '</button>' +
        '</div>';
      return;
    }

    var rows = cart.items
      .map(function (item) {
        var variant =
          item.variant_title && item.variant_title !== 'Default Title'
            ? '<p class="crocart-line__variant">' + esc(item.variant_title) + '</p>'
            : '';

        var sellingPlan = item.selling_plan_allocation
          ? '<p class="crocart-line__variant">' + esc(item.selling_plan_allocation.selling_plan.name) + '</p>'
          : '';

        // Minimum on the stepper is 1 — removal is an explicit action.
        var atMinimum = item.quantity <= 1;

        return (
          '<li class="crocart-line" data-key="' + esc(item.key) + '">' +
          '<a class="crocart-line__media" href="' + esc(item.url) + '" tabindex="-1" aria-hidden="true">' +
          (item.image
            ? '<img src="' + esc(imgUrl(item.image, 200)) + '" alt="" width="88" height="88" loading="lazy">'
            : '<span class="crocart-added__placeholder"></span>') +
          '</a>' +
          '<div class="crocart-line__info">' +
          '<a class="crocart-line__title crocart-line__title--clamp" href="' + esc(item.url) + '" ' +
          'title="' + esc(item.product_title) + '">' + esc(item.product_title) + '</a>' +
          variant +
          sellingPlan +
          '<div class="crocart-qty" role="group" aria-label="' + esc(cfg.qtyLabel) + '">' +
          '<button type="button" class="crocart-qty__btn" data-cc-step="-1" aria-label="' + esc(cfg.decreaseLabel) + '"' +
          (atMinimum ? ' disabled' : '') + '>&minus;</button>' +
          '<input class="crocart-qty__input" type="text" inputmode="numeric" value="' + item.quantity + '" ' +
          'aria-label="' + esc(cfg.qtyLabel) + '" data-cc-qty>' +
          '<button type="button" class="crocart-qty__btn" data-cc-step="1" aria-label="' + esc(cfg.increaseLabel) + '">+</button>' +
          '</div>' +
          '</div>' +
          '<div class="crocart-line__right">' +
          '<p class="crocart-line__price">' + lineCompareHtml(item) + money(item.final_line_price) + '</p>' +
          '<button type="button" class="crocart-line__remove" data-cc-remove>' + esc(cfg.removeLabel) + '</button>' +
          '</div>' +
          '</li>'
        );
      })
      .join('');

    viewFull.innerHTML =
      head +
      '<div class="crocart__body crocart__body--scroll">' +
      '<ul class="crocart-lines">' + rows + '</ul>' +
      '</div>' +
      '<footer class="crocart__foot crocart__foot--full">' +
      discountHtml() +
      '<div class="crocart-totals">' + totalsHtml() + '</div>' +
      '<p class="crocart-note">' + esc(cfg.taxNote) + '</p>' +
      '<button type="button" class="crocart-btn crocart-btn--primary crocart-btn--checkout" data-cc-checkout>' +
      esc(cfg.checkoutLabel) +
      '</button>' +
      '</footer>';
  }

  function renderAll() {
    renderPreview();
    renderFull();
  }

  /* ---------------------------------------------------------------------- */
  /* Header cart bubble                                                      */
  /* ---------------------------------------------------------------------- */

  function updateBubble(count) {
    // Keep Horizon's sessionStorage guard in sync, otherwise cart-icon.js
    // "corrects" our number back to a stale one on the next page view.
    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
    } catch (e) {
      /* private mode — non-fatal */
    }

    document.querySelectorAll('[ref="cartBubbleCount"]').forEach(function (el) {
      el.textContent = count < 100 ? String(count) : '';
      el.classList.toggle('hidden', count === 0);
    });

    document.querySelectorAll('[ref="cartBubble"]').forEach(function (el) {
      el.classList.toggle('visually-hidden', count === 0);
    });

    document.querySelectorAll('cart-icon').forEach(function (el) {
      el.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Network                                                                 */
  /* ---------------------------------------------------------------------- */

  function refresh() {
    return fetch(cfg.rootUrl + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(function (response) {
        return response.json();
      })
      .then(enrich)
      .then(function (data) {
        cart = data;
        updateBubble(cart.item_count);
        renderAll();
        return cart;
      });
  }

  function addToCart(form, button) {
    if (busy) return;
    busy = true;
    if (button) button.setAttribute('aria-busy', 'true');

    fetch(cfg.rootUrl + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form),
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      })
      .then(function (data) {
        var added = data.items ? data.items[data.items.length - 1] : data;
        lastAddedKey = added && added.key;
        return refresh();
      })
      .then(function () {
        open('preview');
      })
      .catch(function (error) {
        showToast((error && (error.description || error.message)) || cfg.errorLabel);
      })
      .finally(function () {
        busy = false;
        if (button) button.removeAttribute('aria-busy');
      });
  }

  /**
   * Changes a line's quantity. Keyed on the line `key`, not the index, so it
   * is immune to line re-ordering — the classic "removed the wrong item" bug.
   */
  function changeLine(key, quantity) {
    if (busy) return;
    busy = true;
    root.setAttribute('data-busy', '');

    fetch(cfg.rootUrl + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      })
      .then(enrich)
      .then(function (data) {
        cart = data;
        updateBubble(cart.item_count);
        renderAll();
      })
      .catch(function (error) {
        showToast((error && (error.description || error.message)) || cfg.errorLabel);
        return refresh();
      })
      .finally(function () {
        busy = false;
        root.removeAttribute('data-busy');
      });
  }

  /* ---------------------------------------------------------------------- */
  /* Toast (errors only)                                                     */
  /* ---------------------------------------------------------------------- */

  var toastTimer = null;

  function showToast(message) {
    if (!toast) return;
    toast.textContent = message;
    toast.setAttribute('data-visible', '');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.removeAttribute('data-visible');
    }, 4000);
  }

  /* ---------------------------------------------------------------------- */
  /* Open / close / state                                                    */
  /* ---------------------------------------------------------------------- */

  function lockScroll() {
    var scrollbar = window.innerWidth - document.documentElement.clientWidth;
    savedScroll.html = document.documentElement.style.overflow;
    savedScroll.body = document.body.style.overflow;
    savedScroll.pad = document.body.style.paddingInlineEnd;

    // Compensate for the scrollbar so the page behind does not shift a pixel.
    document.body.style.paddingInlineEnd = scrollbar + 'px';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    document.documentElement.style.overflow = savedScroll.html;
    document.body.style.overflow = savedScroll.body;
    document.body.style.paddingInlineEnd = savedScroll.pad;
  }

  function setState(next) {
    root.setAttribute('data-state', next);
  }

  function open(state) {
    if (!active()) return;

    killNativeDrawer();
    clearTimeout(closeTimer);
    lastFocused = document.activeElement;

    if (!cart) {
      refresh().then(function () {
        open(state);
      });
      return;
    }

    setState(state || 'preview');
    root.hidden = false;
    lockScroll();

    // Force a reflow so the browser registers the starting transform before
    // we flip the open flag — otherwise the slide-in is skipped entirely.
    void root.offsetWidth;
    root.setAttribute('data-open', '');

    var focusTarget = (state === 'full' ? viewFull : viewPreview).querySelector('[data-cc-close]');
    if (focusTarget) focusTarget.focus({ preventScroll: true });

    document.addEventListener('keydown', onKeydown, true);
  }

  function close() {
    if (root.hidden) return;

    root.removeAttribute('data-open');
    document.removeEventListener('keydown', onKeydown, true);
    unlockScroll();

    closeTimer = setTimeout(function () {
      root.hidden = true;
      setState('preview');
    }, CLOSE_MS);

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus({ preventScroll: true });
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Keyboard: Escape + focus trap                                           */
  /* ---------------------------------------------------------------------- */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    var activeView = root.getAttribute('data-state') === 'full' ? viewFull : viewPreview;
    if (!activeView) return;

    var items = Array.prototype.filter.call(activeView.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Event delegation inside the drawer                                      */
  /* ---------------------------------------------------------------------- */

  function bindDrawer() {
    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-cc-close]') || target.closest('.crocart__backdrop')) {
        event.preventDefault();
        close();
        return;
      }

      if (target.closest('[data-cc-expand]')) {
        event.preventDefault();
        setState('full');
        var closeBtn = viewFull.querySelector('[data-cc-close]');
        if (closeBtn) setTimeout(function () { closeBtn.focus({ preventScroll: true }); }, 200);
        return;
      }

      if (target.closest('[data-cc-checkout]')) {
        event.preventDefault();
        window.location.href = cfg.rootUrl + 'checkout';
        return;
      }

      if (target.closest('[data-cc-disc-toggle]')) {
        event.preventDefault();
        discountOpen = !discountOpen;
        // Toggle in place rather than re-rendering, so the CSS height
        // transition actually runs and focus is not thrown away.
        var disc = viewFull.querySelector('.crocart-disc');
        var toggle = viewFull.querySelector('[data-cc-disc-toggle]');
        if (disc) {
          if (discountOpen) disc.setAttribute('data-open', '');
          else disc.removeAttribute('data-open');
        }
        if (toggle) toggle.setAttribute('aria-expanded', discountOpen ? 'true' : 'false');
        if (discountOpen) {
          var field = viewFull.querySelector('[data-cc-disc-input]');
          if (field) setTimeout(function () { field.focus({ preventScroll: true }); }, 180);
        }
        return;
      }

      if (target.closest('[data-cc-disc-apply]')) {
        event.preventDefault();
        var field = viewFull.querySelector('[data-cc-disc-input]');
        if (field) {
          discountDraft = field.value;
          applyDiscount(field.value);
        }
        return;
      }

      var stepBtn = target.closest('[data-cc-step]');
      if (stepBtn) {
        event.preventDefault();
        var line = stepBtn.closest('.crocart-line');
        var input = line.querySelector('[data-cc-qty]');
        var next = parseInt(input.value, 10) + parseInt(stepBtn.getAttribute('data-cc-step'), 10);
        // Floor of 1: the stepper never deletes a line.
        changeLine(line.getAttribute('data-key'), Math.max(1, next));
        return;
      }

      var removeBtn = target.closest('[data-cc-remove]');
      if (removeBtn) {
        event.preventDefault();
        changeLine(removeBtn.closest('.crocart-line').getAttribute('data-key'), 0);
      }
    });

    // Typed quantity — commit on Enter or blur, clamped to a minimum of 1.
    root.addEventListener('change', function (event) {
      var input = event.target;
      if (!(input instanceof Element) || !input.hasAttribute('data-cc-qty')) return;
      var value = parseInt(input.value, 10);
      if (isNaN(value) || value < 1) value = 1;
      changeLine(input.closest('.crocart-line').getAttribute('data-key'), value);
    });

    // Keep the typed discount code across re-renders.
    root.addEventListener('input', function (event) {
      var input = event.target;
      if (!(input instanceof Element) || !input.hasAttribute('data-cc-disc-input')) return;
      discountDraft = input.value;
    });

    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      var input = event.target;
      if (!(input instanceof Element)) return;

      if (input.hasAttribute('data-cc-qty')) {
        event.preventDefault();
        input.blur();
        return;
      }

      if (input.hasAttribute('data-cc-disc-input')) {
        event.preventDefault();
        discountDraft = input.value;
        applyDiscount(input.value);
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Global interception                                                     */
  /* ---------------------------------------------------------------------- */

  var CART_TRIGGER_SELECTOR = [
    'cart-icon',
    '.header-actions__cart-icon',
    '[on\\:click*="cart-drawer"]',
    '[data-cart-drawer-toggle]',
    'a[href$="/cart"]',
    'a[href*="/cart?"]',
  ].join(', ');

  function bindGlobal() {
    document.addEventListener(
      'click',
      function (event) {
        if (!active()) return;
        // Let modified clicks fall through so cmd/ctrl-click still opens /cart
        // in a new tab. We deliberately do NOT bail on defaultPrevented:
        // Horizon's own capture handler runs first and prevents the default.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        var target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('#CroCart')) return; // handled by bindDrawer

        // 1) Add-to-cart submit buttons.
        var addBtn = target.closest('button[name="add"], input[name="add"], .add-to-cart-button');
        if (addBtn && !addBtn.disabled) {
          var form = addBtn.closest('form[action*="/cart/add"]');
          if (form) {
            event.preventDefault();
            event.stopImmediatePropagation();
            addToCart(form, addBtn);
            return;
          }
        }

        // 2) Header cart icon / any link or toggle pointing at the cart.
        if (target.closest(CART_TRIGGER_SELECTOR)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          killNativeDrawer();
          open('full');
        }
      },
      true
    );

    // Fallback for keyboard submits and programmatic form posts.
    document.addEventListener(
      'submit',
      function (event) {
        if (!active()) return;
        var form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        addToCart(form, form.querySelector('[name="add"]'));
      },
      true
    );

    var wasDesktop = isDesktop();
    window.addEventListener('resize', function () {
      var nowDesktop = isDesktop();
      // Crossing the 990px boundary while open would otherwise leave the
      // panel straddling two incompatible geometries (translateX+width on
      // desktop vs translateY+height on mobile, or no geometry at all once
      // inactive) with no animated path between them — closing is the only
      // clean way to avoid a glitch. This must run regardless of active(),
      // since the boundary that matters here is the one the panel opened
      // under, not whether CroCart is still in charge on the new side of it.
      if (!root.hidden && nowDesktop !== wasDesktop) close();
      if (active()) killNativeDrawer();
      wasDesktop = nowDesktop;
    });

    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        killNativeDrawer();
        refresh();
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Boot                                                                    */
  /* ---------------------------------------------------------------------- */

  function init() {
    root = document.getElementById('CroCart');
    if (!root) return;

    viewPreview = root.querySelector('[data-view="preview"]');
    viewFull = root.querySelector('[data-view="full"]');
    toast = root.querySelector('.crocart__toast');

    cfg = {
      rootUrl: root.dataset.rootUrl || '/',
      moneyFormat: root.dataset.moneyFormat || '${{amount}}',
      shippingEnabled: root.dataset.shippingEnabled === 'true',
      shippingThreshold: parseInt(root.dataset.shippingThreshold, 10) || 0,
      shipUnlocked: root.dataset.shipUnlocked,
      shipProgress: root.dataset.shipProgress,
      shipLabel: root.dataset.shipLabel,
      addedTitle: root.dataset.addedTitle,
      cartTitle: root.dataset.cartTitle,
      subtotalLabel: root.dataset.subtotalLabel,
      beforeDiscountsLabel: root.dataset.beforeDiscountsLabel,
      savingsLabel: root.dataset.savingsLabel,
      viewCartLabel: root.dataset.viewCartLabel,
      checkoutLabel: root.dataset.checkoutLabel,
      continueLabel: root.dataset.continueLabel,
      removeLabel: root.dataset.removeLabel,
      qtyLabel: root.dataset.qtyLabel,
      increaseLabel: root.dataset.increaseLabel,
      decreaseLabel: root.dataset.decreaseLabel,
      emptyTitle: root.dataset.emptyTitle,
      taxNote: root.dataset.taxNote,
      closeLabel: root.dataset.closeLabel,
      errorLabel: root.dataset.errorLabel,
      discountPlaceholder: root.dataset.discountPlaceholder,
      discountToggleLabel: root.dataset.discountToggleLabel,
      applyLabel: root.dataset.applyLabel,
      discountErrorLabel: root.dataset.discountErrorLabel,
    };

    killNativeDrawer();
    watchForNativeDrawer();

    bindDrawer();
    bindGlobal();

    if (active()) refresh();

    window.CroCart = { open: open, close: close, refresh: refresh, killNativeDrawer: killNativeDrawer };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
